import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Store, 
  Book, 
  Users, 
  DollarSign, 
  Award,
  ShoppingCart,
  TrendingUp,
  Clock,
  CheckCircle,
  Building2,
  UserCheck,
  Megaphone,
  Truck,
  Package,
  ClipboardCheck,
  ClipboardList
} from 'lucide-react';
import {
  PageContainer,
  MainContent,
  Grid,
  Spacer
} from '../../styles/StyledComponents';
import { DashboardCard } from '../../components/shared/DashboardCard';
import { StatsCard, StatsCardGrid } from '../../components/shared/StatsCard';
import { useOrders } from '../../hooks/useOrders';
import { useStores } from '../../hooks/useStores';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { AnnouncementCard } from '../../components/announcements/AnnouncementCard';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

const DashboardHeading = styled.h1`
  text-align: left;
  margin-bottom: ${Spacing.xl};
  color: ${ThemeColors.secondary.main};
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.lg};
  margin-top: ${Spacing.xxl};
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};
`;

const QuickActionButton = styled.button`
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: 12px;
  padding: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background-color: ${ThemeColors.primary.light};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const QuickActionText = styled.div`
  flex: 1;
`;

const QuickActionTitle = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: 2px;
`;

const QuickActionSubtitle = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
`;

/**
 * Manager Dashboard Desktop Component
 * Enhanced with statistics and quick actions
 */
export const ManagerDashboardScreenDesktop = React.memo(() => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false); // Expanded by default on desktop
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Use custom hooks for data fetching
  const { 
    statistics: orderStats, 
    isLoadingStatistics: isLoadingOrders,
    fetchStatistics: fetchOrderStats 
  } = useOrders();
  
  const { 
    statistics: storeStats, 
    isLoadingStatistics: isLoadingStores,
    fetchStatistics: fetchStoreStats 
  } = useStores();

  const { 
    announcements, 
    loading: isLoadingAnnouncements 
  } = useAnnouncements({ status: 'ACTIVE' });

  /**
   * Fetch statistics on component mount
   */
  useEffect(() => {
    fetchOrderStats();
    fetchStoreStats();
  }, [fetchOrderStats, fetchStoreStats]);

  const menuItems = useMemo(() => {
    const isHandLead = userRole === 'hand-lead';
    
    const items = [
      // Main Section
      {
        id: 'orders',
        label: 'Order Management',
        icon: ShoppingCart,
        description: 'View, approve, and manage customer orders. Track order status and history.',
        action: () => navigate('/manager/orders')
      },
      {
        id: 'products',
        label: 'Product Management',
        icon: Package,
        description: 'Control product availability. Mark items as out-of-stock to remove them from sales rep orders.',
        action: () => navigate('/manager/products')
      },
      {
        id: 'stores',
        label: 'Store Management',
        icon: Building2,
        description: 'Manage store information, assignments, and customer accounts.',
        action: () => navigate('/manager/stores')
      },
      {
        id: 'analytics',
        label: t('navigation.analytics', 'Analytics & Performance'),
        icon: TrendingUp,
        description: t('dashboard.analyticsDesc', 'View activity logs, performance metrics, and team analytics.'),
        action: () => navigate('/manager/analytics')
      },
    ];
    
    // Operations Section
    // User Management - only for managers, not hand-lead
    if (!isHandLead) {
      items.push({
        id: 'user-management',
        label: t('navigation.userManagement', 'User Management'),
        icon: UserCheck,
        description: t('dashboard.userManagementDesc', 'Manage users, roles, and permissions. Create and edit user accounts.'),
        action: () => navigate('/manager/users')
      });
    }
    
    // Resource Library - different access for hand-lead
    if (isHandLead) {
      items.push({
        id: 'resources',
        label: t('navigation.resources', 'Resource Library'),
        icon: Book,
        description: t('dashboard.resourcesDesc', 'Access training materials, product guides, videos, and documents.'),
        action: () => navigate('/resources')
      });
    } else {
      items.push({
        id: 'resource-management',
        label: t('navigation.resourceManagement', 'Resource Library'),
        icon: Book,
        description: t('dashboard.resourceManagementDesc', 'Create and manage training materials, guides, documents, and videos.'),
        action: () => navigate('/manager/resources')
      });
    }
    
    items.push({
      id: 'store-visit',
      label: t('navigation.storeVisit', 'Store Visit'),
      icon: Store,
      description: t('dashboard.storeVisitDesc', 'Visit stores, manage information, and track customer interactions.'),
      action: () => navigate('/manager/store-visit')
    });
    
    // Shipments - different access for hand-lead
    if (isHandLead) {
      items.push({
        id: 'shipments',
        label: 'Shipments',
        icon: Truck,
        description: 'View shipment notifications and delivery schedules for your sales group.',
        action: () => navigate('/shipments')
      });
    } else {
      items.push({
        id: 'shipments',
        label: 'Shipments',
        icon: Truck,
        description: 'Track and manage shipment notifications, delivery schedules, and logistics.',
        action: () => navigate('/manager/shipments')
      });
    }
    
    // Tools Section
    items.push(
      {
        id: 'payroll',
        label: t('navigation.payroll', 'Payroll'),
        icon: DollarSign,
        description: t('dashboard.payrollDesc', 'View commission reports, payment history, and expense tracking.'),
        action: () => navigate('/manager/payroll')
      },
      {
        id: 'contest-entry',
        label: t('navigation.contestEntry', 'Contest Entry'),
        icon: Award,
        description: t('dashboard.contestEntryDesc', 'Participate in sales contests and view performance rankings.'),
        action: () => navigate('/manager/contest-entry')
      },
    );

    if (isHandLead) {
      items.push({
        id: 'eos-survey',
        label: 'EOS Survey',
        icon: ClipboardList,
        description: 'Complete your End-of-Season survey.',
        action: () => navigate('/manager/eos-survey')
      });
    }

    items.push(
      {
        id: 'tradeshow',
        label: 'Trade Show Orders',
        icon: ClipboardCheck,
        description: 'Review display orders submitted from the public trade show form.',
        action: () => navigate('/manager/tradeshow')
      },
      {
        id: 'announcements',
        label: 'Announcements',
        icon: Megaphone,
        description: 'Create and manage announcements for sales representatives and teams.',
        action: () => navigate('/manager/announcements')
      }
    );
    
    return items;
  }, [t, navigate, userRole]);

  /**
   * Quick action handlers
   */
  const handleViewOrders = () => navigate('/manager/orders');
  const handleViewStores = () => navigate('/manager/stores');
  const handlePendingOrders = () => navigate('/manager/orders?status=submitted');
  const handleManageStores = () => navigate('/manager/stores');

  return (
    <PageContainer isMenuCollapsed={isMenuCollapsed}>
      <MainContent>
        <DashboardHeading>
          {t('dashboard.welcomeBack', 'Welcome Back')} {user?.firstName || user?.username || ''}
        </DashboardHeading>
        
        {/* Statistics Cards - Hidden */}
        {/* <SectionTitle>Overview Statistics</SectionTitle>
        <StatsCardGrid columns={4}>
          <StatsCard
            title="Total Orders"
            value={orderStats?.totalOrders || 0}
            subtitle="All time"
            icon={ShoppingCart}
            iconColor={ThemeColors.primary.main}
            iconBackgroundColor={ThemeColors.primary.light}
            isLoading={isLoadingOrders}
            variant="info"
            onClick={handleViewOrders}
          />
          <StatsCard
            title="Pending Orders"
            value={orderStats?.pendingOrders || 0}
            subtitle="Awaiting review"
            icon={Clock}
            iconColor={ThemeColors.warning.main}
            iconBackgroundColor={ThemeColors.warning.light}
            isLoading={isLoadingOrders}
            variant="warning"
            onClick={handlePendingOrders}
          />
          <StatsCard
            title="Total Revenue"
            value={`$${(orderStats?.totalRevenue || 0).toLocaleString()}`}
            subtitle="All time"
            icon={TrendingUp}
            iconColor={ThemeColors.success.main}
            iconBackgroundColor={ThemeColors.success.light}
            isLoading={isLoadingOrders}
            variant="success"
          />
          <StatsCard
            title="Active Stores"
            value={storeStats?.activeStores || 0}
            subtitle={`${storeStats?.totalStores || 0} total stores`}
            icon={Building2}
            iconColor={ThemeColors.info.main}
            iconBackgroundColor={ThemeColors.info.light}
            isLoading={isLoadingStores}
            variant="info"
            onClick={handleViewStores}
          />
        </StatsCardGrid> */}

        {/* Quick Actions - Hidden */}
        {/* <SectionTitle>Quick Actions</SectionTitle>
        <QuickActionsGrid>
          <QuickActionButton onClick={() => navigate('/manager/orders')}>
            <QuickActionIcon>
              <ShoppingCart />
            </QuickActionIcon>
            <QuickActionText>
              <QuickActionTitle>Manage Orders</QuickActionTitle>
              <QuickActionSubtitle>
                {orderStats?.pendingOrders || 0} pending
              </QuickActionSubtitle>
            </QuickActionText>
          </QuickActionButton>

          <QuickActionButton onClick={() => navigate('/manager/stores')}>
            <QuickActionIcon>
              <Building2 />
            </QuickActionIcon>
            <QuickActionText>
              <QuickActionTitle>Manage Stores</QuickActionTitle>
              <QuickActionSubtitle>
                {storeStats?.totalStores || 0} stores
              </QuickActionSubtitle>
            </QuickActionText>
          </QuickActionButton>

          <QuickActionButton onClick={() => navigate('/manager/orders?status=submitted')}>
            <QuickActionIcon>
              <CheckCircle />
            </QuickActionIcon>
            <QuickActionText>
              <QuickActionTitle>Approve Orders</QuickActionTitle>
              <QuickActionSubtitle>Review & approve</QuickActionSubtitle>
            </QuickActionText>
          </QuickActionButton>

          <QuickActionButton onClick={() => navigate('/manager/users')}>
            <QuickActionIcon>
              <Users />
            </QuickActionIcon>
            <QuickActionText>
              <QuickActionTitle>User Management</QuickActionTitle>
              <QuickActionSubtitle>Manage users & roles</QuickActionSubtitle>
            </QuickActionText>
          </QuickActionButton>
        </QuickActionsGrid> */}

        {/* Main Navigation Cards */}
        <SectionTitle>Management Tools</SectionTitle>
        <Grid minWidth="250px" gap={Spacing.xl}>
          {menuItems.map((item) => (
            <DashboardCard
              key={item.id}
              id={item.id}
              label={item.label}
              description={item.description}
              icon={item.icon}
              onClick={item.action}
              variant="desktop"
            />
          ))}
        </Grid>

        {/* Announcements Section */}
        {announcements.length > 0 && (
          <>
            <SectionTitle>
              <Megaphone style={{ display: 'inline', marginRight: Spacing.sm, verticalAlign: 'middle' }} />
              Recent Announcements
            </SectionTitle>
            <Grid minWidth="300px" gap={Spacing.md}>
              {announcements.slice(0, 3).map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  variant="desktop"
                  onView={(id) => navigate(`/manager/announcements/${id}`)}
                />
              ))}
            </Grid>
            {announcements.length > 3 && (
              <div style={{ textAlign: 'center', marginTop: Spacing.md }}>
                <QuickActionButton onClick={() => navigate('/manager/announcements')}>
                  View All Announcements ({announcements.length})
                </QuickActionButton>
              </div>
            )}
          </>
        )}
        
        <Spacer height={Spacing.xxl} />
      </MainContent>
    </PageContainer>
  );
});

ManagerDashboardScreenDesktop.displayName = 'ManagerDashboardScreenDesktop';